import {
  ConsentStatus,
  type GroupedSelectOptions,
  getIsSignStatus
} from '@lmc/common/types'
import type { HomeDto } from '@lmc/ts-common'
import {
  Button,
  Card,
  Divider,
  Form,
  Icon,
  InputDatePicker,
  InputSelect,
  InputText
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { getNominative } from 'common/utils'
import { NAMESPACE } from 'locale'
import { consentConstants } from 'locale/consent'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

const STATUS_INFO_TEXT_KEYS: Record<ConsentStatus, string> = {
  [ConsentStatus.noStatus]: consentConstants.INFO_NO_STATUS,
  [ConsentStatus.consentNotObtained]:
    consentConstants.INFO_CONSENT_NOT_OBTAINED,
  [ConsentStatus.completed]: consentConstants.INFO_COMPLETED,
  [ConsentStatus.planned]: consentConstants.INFO_PLANNED,
  [ConsentStatus.requested]: consentConstants.INFO_REQUESTED,
  [ConsentStatus.scheduled]: consentConstants.INFO_SCHEDULED,
  [ConsentStatus.postponed]: consentConstants.INFO_POSTPONED,
  [ConsentStatus.cancelled]: consentConstants.INFO_CANCELLED,
  [ConsentStatus.withdrawn]: consentConstants.INFO_WITHDRAWN,
  [ConsentStatus.refused]: consentConstants.INFO_REFUSED,
  [ConsentStatus.provided]: consentConstants.INFO_PROVIDED,
  [ConsentStatus.pending]: consentConstants.INFO_PENDING
}

type StatusOptionConfig = {
  labelKey?: string
  options: {
    labelKey: string
    value: ConsentStatus
  }[]
}

const STATUS_OPTION_GROUPS: StatusOptionConfig[] = [
  {
    options: [
      {
        labelKey: consentConstants.OPTION_NO_STATUS,
        value: ConsentStatus.noStatus
      }
    ]
  },
  {
    labelKey: consentConstants.OPTION_SAVE_FORM,
    options: [
      {
        labelKey: consentConstants.OPTION_CONSENT_NOT_OBTAINED,
        value: ConsentStatus.consentNotObtained
      },
      {
        labelKey: consentConstants.OPTION_COMPLETED,
        value: ConsentStatus.completed
      },
      {
        labelKey: consentConstants.OPTION_PLANNED,
        value: ConsentStatus.planned
      },
      {
        labelKey: consentConstants.OPTION_REQUESTED,
        value: ConsentStatus.requested
      },
      {
        labelKey: consentConstants.OPTION_SCHEDULED,
        value: ConsentStatus.scheduled
      },
      {
        labelKey: consentConstants.OPTION_POSTPONED,
        value: ConsentStatus.postponed
      }
    ]
  },
  {
    labelKey: consentConstants.OPTION_SIGN_FORM,
    options: [
      {
        labelKey: consentConstants.OPTION_CANCELLED,
        value: ConsentStatus.cancelled
      },
      {
        labelKey: consentConstants.OPTION_WITHDRAWN,
        value: ConsentStatus.withdrawn
      },
      {
        labelKey: consentConstants.OPTION_REFUSED,
        value: ConsentStatus.refused
      },
      {
        labelKey: consentConstants.OPTION_PROVIDED,
        value: ConsentStatus.provided
      }
    ]
  }
]

type ConsentConfirmationCardProps = {
  onSubmit?: () => void
  onStatusChange?: ({
    status,
    reason,
    lastReviewDate
  }: {
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }) => void
  selectedStatus: {
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }
  testId?: string
  disabled?: boolean
  signButtonLabel?: string
  confirmButtonLabel?: string
}

const ConsentConfirmationCard: React.FC<ConsentConfirmationCardProps> = ({
  onSubmit,
  onStatusChange,
  selectedStatus,
  testId,
  disabled = false,
  signButtonLabel,
  confirmButtonLabel
}) => {
  const { t } = useTranslation(NAMESPACE.CONSENT)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: HomeDto } }) =>
      state.global.home.preferredPeopleNominative
  )

  const preferredNominative = getNominative(
    preferredPeopleNominative,
    'resident'
  ).toLocaleLowerCase()

  const [internalStatus, setInternalStatus] = useState<ConsentStatus>(
    ConsentStatus.noStatus
  )
  const [lastReviewDate, setLastReviewDate] = useState<string | null>(null)
  const [reason, setReason] = useState<string>('')

  const currentStatus = selectedStatus ?? { status: internalStatus }

  const isSignStatus = getIsSignStatus(currentStatus.status)

  const handleStatusChange = (value: string) => {
    setReason('')
    setLastReviewDate(null)
    const statusValue = value as ConsentStatus
    setInternalStatus(statusValue)

    if (onStatusChange) {
      onStatusChange({
        status: statusValue,
        reason,
        lastReviewDate: lastReviewDate as string
      })
    }
  }

  const getInfoText = (): string => {
    const safeStatus = currentStatus?.status ?? ConsentStatus.noStatus
    return t(STATUS_INFO_TEXT_KEYS[safeStatus])
  }

  const baseOptions =
    STATUS_OPTION_GROUPS.find((group) => !group.labelKey)?.options ?? []

  const groupedOptionConfigs = STATUS_OPTION_GROUPS.filter(
    (group) => !!group.labelKey
  )

  const options = baseOptions.map((option) => ({
    label: t(option.labelKey),
    value: option.value
  }))

  const groupedOptions: GroupedSelectOptions = groupedOptionConfigs.map(
    (group) => ({
      label: group.labelKey ? t(group.labelKey) : '',
      options: group.options.map((option) => ({
        label: t(option.labelKey),
        value: option.value
      }))
    })
  )

  const getButtonLabel = (): string => {
    if (isSignStatus) {
      return signButtonLabel ?? t(consentConstants.SIGN_BUTTON)
    }
    return confirmButtonLabel ?? t(consentConstants.CONFIRM_BUTTON)
  }

  return (
    <StyledCard
      data-testid={
        testId
          ? `${testId}-consent-confirmation-card`
          : 'consent-confirmation-card'
      }
    >
      {onSubmit && (
        <>
          <TopSection>
            <QuestionText level={1}>
              {t(consentConstants.QUESTION, {
                nominative: preferredNominative
              })}
            </QuestionText>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={disabled}
              data-testid={
                testId
                  ? `${testId}-confirm-button`
                  : 'consent-confirmation-confirm-button'
              }
            >
              <Icon name="CheckOutlined" size={16} />
              <Space>{getButtonLabel()}</Space>
            </Button>
          </TopSection>

          <StyledDivider />
        </>
      )}

      <StatusSection>
        <LabelContainer>
          <LabelText level={1}>
            {t(consentConstants.CONSENT_STATUS_LABEL)}
          </LabelText>
          <Asterisk>*</Asterisk>
        </LabelContainer>

        <HelperText level={2}>{t(consentConstants.HELPER_TEXT)}</HelperText>

        <SelectContainer>
          <Form>
            <StyledInputSelect
              testId={
                testId
                  ? `${testId}-consent-status-select`
                  : 'consent-status-select'
              }
              options={options}
              value={currentStatus.status}
              onChange={handleStatusChange}
              groupedOptions={groupedOptions}
              layout="column"
              defaultValue={ConsentStatus.noStatus}
              formItemstyle={selectFormItemStyle}
              listHeight={350}
            />
          </Form>
        </SelectContainer>
        <InfoText>{getInfoText()}</InfoText>

        {currentStatus?.status === ConsentStatus.postponed && (
          <ExtraSection>
            <LabelContainer>
              <LabelText level={1}>
                {t(consentConstants.POSTPONED_LABEL)}
              </LabelText>
            </LabelContainer>
            <HelperText level={2}>
              {t(consentConstants.POSTPONED_HELPER)}
            </HelperText>
            <div data-testid="consent-confirmation-postponed-date">
              <StyledInputDatePicker
                onChange={(date: unknown) => {
                  const dateValue = date as string | null
                  setLastReviewDate(dateValue)
                  if (onStatusChange) {
                    onStatusChange({
                      status: currentStatus.status,
                      reason: currentStatus.reason,
                      lastReviewDate: dateValue
                    })
                  }
                }}
                wrapFormItem={false}
                value={currentStatus.lastReviewDate || null}
                localization={{
                  placeholder: t(consentConstants.POSTPONED_PLACEHOLDER),
                  errorMessage: ''
                }}
              />
            </div>
          </ExtraSection>
        )}

        {currentStatus?.status === ConsentStatus.withdrawn && (
          <ExtraSection>
            <LabelContainer>
              <LabelText level={1}>
                {t(consentConstants.WITHDRAWN_LABEL)}
              </LabelText>
              <Asterisk>*</Asterisk>
            </LabelContainer>
            <HelperText level={2}>
              {t(consentConstants.WITHDRAWN_HELPER)}
            </HelperText>
            <div data-testid="consent-confirmation-withdrawn-reason">
              <StyledInputText
                onChange={({ target }) => {
                  setReason(target.value)
                  if (onStatusChange) {
                    onStatusChange({
                      status: currentStatus.status,
                      reason: target.value,
                      lastReviewDate: currentStatus.lastReviewDate
                    })
                  }
                }}
                value={currentStatus.reason || ''}
                layout="column"
                placeholder={t(consentConstants.WITHDRAWN_PLACEHOLDER)}
              />
            </div>
          </ExtraSection>
        )}
      </StatusSection>
    </StyledCard>
  )
}

const StyledCard = styled(Card)`
  padding: 24px;
`

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const Space = styled.span`
  margin-left: 8px;
`

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
`

const LabelContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
`

const Asterisk = styled.span`
  color: red;
  margin-left: 2px;
`

const SelectContainer = styled.div``

const ExtraSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const StyledInputText = styled(InputText)`
  width: 390px;
  max-width: 100%;
  height: 36px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

const StyledInputDatePicker = styled(InputDatePicker)`
  width: 390px;
  max-width: 100%;
  .ant-picker {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

const QuestionText = styled(Text)`
  font-size: 24px;
  font-weight: 500;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    font-size: 18px;
  }
`

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const LabelText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
`

const HelperText = styled(Text)`
  font-size: 12px;
  margin-bottom: 16px;
`

const StyledInputSelect = styled(InputSelect)`
  width: 390px;
  max-width: 100%;
  height: 36px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

const InfoText = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.color.neutral.color4};
  max-width: 390px;
  word-wrap: break-word;
  white-space: normal;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    max-width: 100%;
  }
`

const selectFormItemStyle = {
  marginBottom: 0
}

export default ConsentConfirmationCard
